/**
 * Saved Scenes Component
 *
 * Gallery view of user's saved scenes with sharing capabilities.
 * Allows users to view, load, share, and delete their saved scenes.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import navigationService from '../services/navigationService';
import parallaxService from '../services/parallaxService';
import logger from '../utils/logger';
import './SavedScenes.css';

const SavedScenes = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // ID of scene to delete
  const [shareToast, setShareToast] = useState(null); // Scene ID being shared

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      logger.log('⚠️ User not authenticated, redirecting to home');
      navigationService.navigateTo('home');
    }
  }, [authLoading, isAuthenticated]);

  // Load user's saved scenes
  useEffect(() => {
    const loadScenes = async () => {
      if (!isAuthenticated || !user?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch user's saved scenes from backend
        const userScenes = await parallaxService.getUserScenes(user.uid);

        // Sort by updated date (most recent first)
        const sortedScenes = (userScenes || []).sort((a, b) => {
          const dateA = new Date(b.updatedAt || b.createdAt);
          const dateB = new Date(a.updatedAt || a.createdAt);
          return dateA - dateB;
        });

        setScenes(sortedScenes);
        logger.log(`🎨 Loaded ${sortedScenes.length} saved scenes`);
      } catch (err) {
        console.error('❌ Error loading scenes:', err);
        setError('Unable to load saved scenes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadScenes();
    }
  }, [isAuthenticated, user, authLoading]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Load scene in scene builder
  const handleLoadScene = (scene) => {
    logger.log('🎨 Loading scene:', scene.name);
    // Navigate to scene builder and load the scene
    navigationService.navigateTo('scene-builder');
    // The scene builder will detect the scene ID in the URL or state
    setTimeout(() => {
      parallaxService.loadScene(scene.id);
    }, 100);
  };

  // Share scene - copy URL to clipboard
  const handleShareScene = async (scene) => {
    const shareUrl = `${window.location.origin}/scene/${scene.id}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareToast(scene.id);
      setTimeout(() => setShareToast(null), 3000);
      logger.log('🔗 Scene share URL copied:', shareUrl);
    } catch (error) {
      console.error('❌ Failed to copy share URL:', error);

      // Fallback: try native share API
      if (navigator.share) {
        try {
          await navigator.share({
            title: scene.name,
            text: `Check out my scene: ${scene.name}`,
            url: shareUrl,
          });
        } catch (shareError) {
          console.error('❌ Native share failed:', shareError);
        }
      }
    }
  };

  // Delete scene
  const handleDeleteScene = async (sceneId) => {
    try {
      await parallaxService.deleteScene(sceneId);
      setScenes(scenes.filter((s) => s.id !== sceneId));
      setDeleteConfirm(null);
      logger.log('🗑️ Scene deleted:', sceneId);
    } catch (error) {
      console.error('❌ Error deleting scene:', error);
      alert('Failed to delete scene. Please try again.');
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <>
        <MainHeader currentPage="scenes" />
        <div className="saved-scenes loading">
          <div className="loading-message">Loading your scenes...</div>
        </div>
      </>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <MainHeader currentPage="scenes" />
      <div className="saved-scenes">
        <div className="scenes-container">
          {/* Page Header */}
          <div className="scenes-header">
            <div className="scenes-header-content">
              <h1>My Saved Scenes</h1>
              <p className="scenes-subtitle">View, load, and share your saved sticker scenes</p>
            </div>
            <button
              className="create-scene-btn"
              onClick={() => navigationService.navigateTo('scene-builder')}
            >
              <MaterialIcon icon="add" size={20} />
              Create New Scene
            </button>
          </div>

          {/* Error State */}
          {error && (
            <div className="scenes-error">
              <MaterialIcon icon="error" size={32} />
              <p>{error}</p>
              <button className="retry-btn" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!error && scenes.length === 0 && (
            <div className="scenes-empty">
              <MaterialIcon icon="palette" size={80} />
              <h2>No Saved Scenes Yet</h2>
              <p>
                Create your first scene in the Scene Builder! Arrange stickers, save your work, and
                share it with others.
              </p>
              <button
                className="create-first-scene-btn"
                onClick={() => navigationService.navigateTo('scene-builder')}
              >
                <MaterialIcon icon="add_circle" size={20} />
                Create Your First Scene
              </button>
            </div>
          )}

          {/* Scenes Grid */}
          {!error && scenes.length > 0 && (
            <div className="scenes-grid">
              {scenes.map((scene) => (
                <div key={scene.id} className="scene-card">
                  {/* Scene Preview/Thumbnail */}
                  <div className="scene-thumbnail" onClick={() => handleLoadScene(scene)}>
                    {scene.thumbnail ? (
                      <img src={scene.thumbnail} alt={scene.name} />
                    ) : (
                      <div className="thumbnail-placeholder">
                        <MaterialIcon icon="image" size={48} />
                        <span>No Preview</span>
                      </div>
                    )}
                    <div className="thumbnail-overlay">
                      <MaterialIcon icon="visibility" size={24} />
                      <span>View Scene</span>
                    </div>
                  </div>

                  {/* Scene Info */}
                  <div className="scene-info">
                    <h3 className="scene-name">{scene.name}</h3>
                    <div className="scene-meta">
                      <div className="meta-item">
                        <MaterialIcon icon="layers" size={14} />
                        <span>{scene.stickerCount || 0} stickers</span>
                      </div>
                      <div className="meta-item">
                        <MaterialIcon icon="schedule" size={14} />
                        <span>{formatDate(scene.updatedAt || scene.createdAt)}</span>
                      </div>
                    </div>

                    {/* Scene Actions */}
                    <div className="scene-actions">
                      <button
                        className="action-btn action-btn-primary"
                        onClick={() => handleLoadScene(scene)}
                        title="Load in Scene Builder"
                      >
                        <MaterialIcon icon="edit" size={18} />
                        Edit
                      </button>
                      <button
                        className="action-btn action-btn-share"
                        onClick={() => handleShareScene(scene)}
                        title="Copy share link"
                      >
                        <MaterialIcon icon="share" size={18} />
                        Share
                      </button>
                      <button
                        className="action-btn action-btn-delete"
                        onClick={() => setDeleteConfirm(scene.id)}
                        title="Delete scene"
                      >
                        <MaterialIcon icon="delete" size={18} />
                      </button>
                    </div>

                    {/* Share Toast */}
                    {shareToast === scene.id && (
                      <div className="share-success-toast">
                        <MaterialIcon icon="check_circle" size={16} />
                        Link copied!
                      </div>
                    )}
                  </div>

                  {/* Delete Confirmation */}
                  {deleteConfirm === scene.id && (
                    <div className="delete-confirm-overlay">
                      <div className="delete-confirm-dialog">
                        <MaterialIcon icon="warning" size={32} />
                        <h4>Delete Scene?</h4>
                        <p>Are you sure you want to delete "{scene.name}"?</p>
                        <p className="delete-warning">This action cannot be undone.</p>
                        <div className="delete-confirm-actions">
                          <button
                            className="cancel-delete-btn"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </button>
                          <button
                            className="confirm-delete-btn"
                            onClick={() => handleDeleteScene(scene.id)}
                          >
                            <MaterialIcon icon="delete" size={18} />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SavedScenes;
